import React from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { CalendarIcon, X, SlidersHorizontal } from 'lucide-react';
import { format } from 'date-fns';

export const FilterBar = ({ filters, onChange, locations, referralSources, paymentMethods }) => {
  const [showCalFrom, setShowCalFrom] = React.useState(false);
  const [showCalTo, setShowCalTo] = React.useState(false);

  const setFilter = (key, val) => onChange({ ...filters, [key]: val });

  const presets = [
    { label: 'Last 7d', days: 7 },
    { label: 'Last 30d', days: 30 },
    { label: 'This Month', type: 'month' },
    { label: 'This Year', type: 'year' },
  ];

  const applyPreset = (preset) => {
    const now = new Date();
    let from;
    if (preset.days) {
      from = new Date(now); from.setDate(from.getDate() - preset.days);
    } else if (preset.type === 'month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      from = new Date(now.getFullYear(), 0, 1);
    }
    onChange({ ...filters, date_from: format(from, 'yyyy-MM-dd'), date_to: format(now, 'yyyy-MM-dd') });
  };

  const hasActiveFilters = filters.location_id || filters.referral_source || filters.payment_method;

  return (
    <div className="surface-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 mr-1">
          <SlidersHorizontal size={13} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Filters</span>
        </div>

        {/* Presets */}
        {presets.map(p => (
          <button key={p.label} onClick={() => applyPreset(p)}
            className="text-xs px-2.5 py-1.5 rounded-lg font-medium border border-slate-200 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50 transition-colors">
            {p.label}
          </button>
        ))}

        <div className="w-px h-4 bg-slate-200 mx-1" />

        {/* Date From */}
        <Popover open={showCalFrom} onOpenChange={setShowCalFrom}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs gap-1.5 px-3" data-testid="dashboard-filter-date-from">
              <CalendarIcon size={12} className="text-slate-400" />
              {filters.date_from ? format(new Date(filters.date_from + 'T00:00:00'), 'MMM d, yyyy') : 'From'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white rounded-xl shadow-[0_4px_24px_rgba(15,23,42,0.12)] border-slate-200">
            <Calendar mode="single"
              selected={filters.date_from ? new Date(filters.date_from + 'T00:00:00') : undefined}
              onSelect={(d) => { if (d) { setFilter('date_from', format(d, 'yyyy-MM-dd')); setShowCalFrom(false); } }}
            />
          </PopoverContent>
        </Popover>

        {/* Date To */}
        <Popover open={showCalTo} onOpenChange={setShowCalTo}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs gap-1.5 px-3" data-testid="dashboard-filter-date-to">
              <CalendarIcon size={12} className="text-slate-400" />
              {filters.date_to ? format(new Date(filters.date_to + 'T00:00:00'), 'MMM d, yyyy') : 'To'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white rounded-xl shadow-[0_4px_24px_rgba(15,23,42,0.12)] border-slate-200">
            <Calendar mode="single"
              selected={filters.date_to ? new Date(filters.date_to + 'T00:00:00') : undefined}
              onSelect={(d) => { if (d) { setFilter('date_to', format(d, 'yyyy-MM-dd')); setShowCalTo(false); } }}
            />
          </PopoverContent>
        </Popover>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        {/* Location */}
        <Select value={filters.location_id || 'all'}
          onValueChange={(v) => setFilter('location_id', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 text-xs w-36" data-testid="dashboard-filter-location">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Referral */}
        {referralSources && (
          <Select value={filters.referral_source || 'all'}
            onValueChange={(v) => setFilter('referral_source', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-36">
              <SelectValue placeholder="All Referrals" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Referrals</SelectItem>
              {referralSources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Payment */}
        {paymentMethods && (
          <Select value={filters.payment_method || 'all'}
            onValueChange={(v) => setFilter('payment_method', v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-36">
              <SelectValue placeholder="All Payments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payments</SelectItem>
              {paymentMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Clear */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500 hover:text-red-500"
            onClick={() => onChange({ ...filters, location_id: '', referral_source: '', payment_method: '' })}>
            <X size={12} className="mr-1" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
};
